import { State, Action, StateContext, Selector } from '@ngxs/store';
import { OrganizationsStateModel } from './organizations.state.model';
import {
  SelectOrganization,
  SetOrganizations,
  SelectCollection,
  GetCollectionsSuccess,
  GetCollections,
  JoinOrganization,
  JoinOrganizationSuccess,
  JoinOrganizationFail,
  ResetOrganizations,
  JoinOrganizationWithCode,
  JoinOrganizationWithCodeFail,
  JoinOrganizationWithCodeSuccess,
  SendOrganizationInvite,
  SendOrganizationInviteSuccess,
  SendOrganizationInviteFail,
  GetOrganizationUsers,
  GetOrganizationUsersSuccess,
  GetOrganizationUsersFail,
  UpdateUserRoles,
  RemoveOrganizationUser,
  RemoveOrganizationUserFail,
  RemoveOrganizationUserSuccess,
  UpdateUserRolesSuccess,
  UpdateUserRolesFail,
  TransferOrganizationContent,
  TransferOrganizationContentSuccess,
  TransferOrganizationContentFail,
  CreateOrganization,
  UpdateOrganization,
  CreateOrganizationSuccess,
  CreateOrganizationFail,
  UpdateOrganizationSuccess,
  UpdateOrganizationFail,
  GetOrganizationProfile,
  GetOrganizationProfileSuccess,
  GetOrganizationProfileFail,
  ClearOrganizationProfile
} from './organizations.actions';
import { OrganizationsService } from '../organizations.service';
import { WidgetOptions } from 'src/app/publish/models/widget-options.model';
import { PublishService } from 'src/app/publish/publish.service';
import { SetPublishWidgetURL } from 'src/app/publish/state/publish.actions';
import { Injectable } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { PendoService } from 'src/app/shared/pendo.service';
import { TeamAccountSetting } from 'src/app/auth/models/account-settings.model';
import { OrganizationAttributeModel } from '../models/organization-attribute.model';
import { get, isNil } from 'lodash';

const initialState: OrganizationsStateModel = {
  creating: false,
  updating: false,
  joining: false,
  inviting: false,
  loadingUsers: false,
  users: []
};

@State<OrganizationsStateModel>({
  name: 'organizations',
  defaults: initialState
})
@Injectable()
export class OrganizationsState {
  /**
   * Selectors
   */
  @Selector()
  static getOrganizations(state: OrganizationsStateModel) {
    return state.organizations;
  }

  @Selector()
  static getOrganization(state: OrganizationsStateModel) {
    return state.organization;
  }

  @Selector()
  static getOrganizationLogo(state: OrganizationsStateModel): string {
    return (state.organization) ? state.organization.group_logos.default : '';
  }

  @Selector()
  static getSelectedOrganization(state: OrganizationsStateModel) {
    return state.selectedOrganization;
  }

  @Selector()
  static getOrganizationProfile(state: OrganizationsStateModel) {
    return state.organizationProfile;
  }

  @Selector()
  static getOrganizationSettings(state: OrganizationsStateModel): TeamAccountSetting[] {
    // get attributes
    const attributes: OrganizationAttributeModel[] = get(state, 'organization.group_attributes', []);
    const settings = attributes.filter((att) => att.type === 'account_settings');

    // map to settings
    return settings.map((setting) => {
      const { name, value } = setting;
      //#TODO[AC]: tidy conversion
      return <TeamAccountSetting><unknown>{
        [name]: {
          value
        }
      };
    });
  }

  @Selector()
  static getUsers(state: OrganizationsStateModel) {
    return state.users;
  }

  @Selector()
  static getCollections(state: OrganizationsStateModel) {
    return state.collections;
  }

  @Selector()
  static getSelectedCollection(state: OrganizationsStateModel) {
    return state.selectedCollection;
  }

  @Selector()
  static getWidgetURL(state: OrganizationsStateModel) {
    return state.widgetURL;
  }

  @Selector()
  static getWidgetOptions(state: OrganizationsStateModel) {
    return state.widgetOptions;
  }

  @Selector()
  static getAppFeatures(state: OrganizationsStateModel) {
    return state.appFeatures;
  }

  @Selector()
  static getCreating(state: OrganizationsStateModel) {
    return state.creating;
  }

  @Selector()
  static getUpdating(state: OrganizationsStateModel) {
    return state.updating;
  }

  @Selector()
  static getJoining(state: OrganizationsStateModel) {
    return state.joining;
  }

  @Selector()
  static getInviting(state: OrganizationsStateModel) {
    return state.inviting;
  }

  @Selector()
  static getUsersLoading(state: OrganizationsStateModel) {
    return state.loadingUsers;
  }

  @Selector()
  static hasOrganization(state: OrganizationsStateModel) {
    return !isNil(state.organization);
  }

  @Selector()
  static isTrial(state: OrganizationsStateModel) {
    const subscriptionType: string = get(state, 'organization.subscription.type', '');
    return subscriptionType.includes('Trial');
  }

  @Selector()
  static isTrialExpired(state: OrganizationsStateModel) {
    return get(state, 'organization.subscription.expired', false) === true;
  }

  
  @Selector()
  static getSubscriptionEndDate(state: OrganizationsStateModel) {
    const paidThoughDate = get(state, 'organization.subscription.paid_through_date');
    return !isNil(paidThoughDate)
      ? new Date(paidThoughDate)
      : undefined;
  }

  /**
   * Actions
   */
  @Action(SelectOrganization)
  selectOrganization(context: StateContext<OrganizationsStateModel>, action: SelectOrganization) {
    const organization = action.organization;
    context.patchState({
      selectedOrganization: organization,
      selectedCollection: null
    });

    // Update collections
    if (organization) {
      context.dispatch(new GetCollections(organization.id));
    }
  }

  @Action(SetOrganizations)
  setOrganizations(context: StateContext<OrganizationsStateModel>, action: SetOrganizations) {
    const organizations = action.organizations || [];
    const organization = organizations[0];

    let widgetURL: string;
    let widgetOptions: WidgetOptions = null;
    let appFeatures = { ... context.getState().appFeatures };

    // read organization widget settings
    if (organization && organization.group_attributes) {
      const attributes = organization.group_attributes;

     // set org default widget url
     const widgetURLAttribute = this.organizationsService.getOrganizationAttribute(attributes, 'widget_settings', 'widget_custom_url_path');
      if (widgetURLAttribute && widgetURLAttribute.value) {
        widgetURL = widgetURLAttribute.value;
      }

      // set org default options
      const widgetOptionsAttribute = this.organizationsService.getOrganizationAttribute(attributes, 'widget_settings', 'widget_url_params');
      if (widgetOptionsAttribute && widgetOptionsAttribute.value) {
        widgetOptions = this.publishService.extendWidgetOptions(new WidgetOptions(), widgetOptionsAttribute.value);
      }

      // set org app features
      const appFeaturesAttribute = this.organizationsService.getOrganizationAttribute(attributes, 'app_settings', 'web_app_features');
      if (appFeaturesAttribute) {
        appFeatures = {
          ...appFeatures,
          ...appFeaturesAttribute.value
        };
      }
    }

    context.patchState({
      organization: organization,
      organizations: organizations,
      widgetURL: widgetURL,
      widgetOptions: widgetOptions,
      appFeatures: appFeatures
    });

    // set default widget
    context.dispatch(new SetPublishWidgetURL(widgetURL));
  }

  @Action(JoinOrganization)
  joinOrganization(context: StateContext<OrganizationsStateModel>, { organization }: JoinOrganization) {
    context.patchState({ joining: true });

    this.organizationsService.addUserToOrganization(organization.eid)
      .pipe(finalize(() => context.patchState({ joining: false })))
      .subscribe(
        () => context.dispatch(new JoinOrganizationSuccess(organization)),
        (err) => context.dispatch(new JoinOrganizationFail(organization))
      );
  }

  @Action(JoinOrganizationWithCode)
  joinOrganizationWithCode(context: StateContext<OrganizationsStateModel>, { code }: JoinOrganizationWithCode) {
    context.patchState({ joining: true });

    this.organizationsService.addUserWithGroupCode(code)
      .pipe(finalize(() => context.patchState({ joining: false })))
      .subscribe(
        (organizationName) => context.dispatch(new JoinOrganizationWithCodeSuccess(organizationName)),
        (err) => context.dispatch(new JoinOrganizationWithCodeFail(err))
      );
  }

  @Action(JoinOrganizationWithCodeSuccess)
  joinOrganizationWithCodeSuccess(context: StateContext<OrganizationsStateModel>) {
    this.pendoService.track('Account Upgrade - Free Individual to Team');
  }

  @Action(SendOrganizationInvite)
  sendOrganizationInvite(context: StateContext<OrganizationsStateModel>, { groupEID, emails }: SendOrganizationInvite) {
    context.patchState({ inviting: true });

    this.organizationsService.sendOrganizationInvites(groupEID, emails)
      .pipe(finalize(() => context.patchState({ inviting: false })))
      .subscribe(
        (responses) => context.dispatch(new SendOrganizationInviteSuccess(responses)),
        (err) => context.dispatch(new SendOrganizationInviteFail(err))
      );
  }

  @Action(GetOrganizationUsers)
  getOrganizationUsers(context: StateContext<OrganizationsStateModel>, { groupId }: GetOrganizationUsers) {
    context.patchState({ loadingUsers: true });

    this.organizationsService.getOrganizationUsers({
      group_id: groupId
    })
      .pipe(finalize(() => context.patchState({ loadingUsers: false })))
      .subscribe(
        (users) => context.dispatch(new GetOrganizationUsersSuccess(users)),
        (err) => context.dispatch(new GetOrganizationUsersFail(err))
      );
  }

  @Action(GetOrganizationUsersSuccess)
  getOrganizationUsersSuccess(context: StateContext<OrganizationsStateModel>, { users }: GetOrganizationUsersSuccess) {
    context.patchState({
      users 
    });
  }

  @Action(GetCollections)
  getCollections(context: StateContext<OrganizationsStateModel>, action: GetCollections) {
    this.organizationsService.getCollections(action.organizationId)
      .subscribe((collections) => {
        context.dispatch(new GetCollectionsSuccess(collections));
      });
  }

  @Action(GetCollectionsSuccess)
  getCollectionsSuccess(context: StateContext<OrganizationsStateModel>, action: GetCollectionsSuccess) {
    context.patchState({
      collections: action.collections
    });
  }

  @Action(SelectCollection)
  selectCollection(context: StateContext<OrganizationsStateModel>, action: SelectCollection) {
    const { organization, selectedOrganization } = context.getState();
    const collection = action.collection;

    context.patchState({
      // ensure organization selected
      selectedOrganization: selectedOrganization || organization,
      selectedCollection: collection
    });
  }

  @Action(ResetOrganizations)
  resetOrganizations(context: StateContext<OrganizationsStateModel>, action: ResetOrganizations) {
    context.setState(initialState);
  }

  @Action(UpdateUserRoles)
  updateUserRoles(context: StateContext<OrganizationsStateModel>, { groupId, users }: UpdateUserRoles) {
    this.organizationsService.updateUserRoles(groupId, users)
      .subscribe(
        () => context.dispatch(new UpdateUserRolesSuccess(groupId, users)),
        () => context.dispatch(new UpdateUserRolesFail())
      );
  }

  @Action(RemoveOrganizationUser)
  removeOrganizationUser(context: StateContext<OrganizationsStateModel>, { groupId, users }: RemoveOrganizationUser) {
    this.organizationsService.removeUsers(groupId, users)
      .subscribe(
        () => context.dispatch(new RemoveOrganizationUserSuccess(groupId, users)),
        () => context.dispatch(new RemoveOrganizationUserFail())
      );
  }

  @Action(TransferOrganizationContent)
  transferOrganizationContent(context: StateContext<OrganizationsStateModel>, { groupId, users}: TransferOrganizationContent) {
    this.organizationsService.transferUserContent(groupId, users)
      .subscribe(
        () => context.dispatch(new TransferOrganizationContentSuccess(groupId, users)),
        () => context.dispatch(new TransferOrganizationContentFail())
      );
  }

  @Action(CreateOrganization)
  createOrganization(context: StateContext<OrganizationsStateModel>, { organization }: CreateOrganization) {
    context.patchState({ creating: true });
    
    this.organizationsService.createOrganization(organization)
    .pipe(finalize(() => context.patchState({ creating: false })))
      .subscribe(
        () => context.dispatch(new CreateOrganizationSuccess()),
        (err) => context.dispatch(new CreateOrganizationFail(err))
      );
  }

  @Action(UpdateOrganization)
  updateOrganization(context: StateContext<OrganizationsStateModel>, { organization }: UpdateOrganization) {
    context.patchState({ updating: true });
    
    this.organizationsService.updateOrganization(organization)
    .pipe(finalize(() => context.patchState({ updating: false })))
      .subscribe(
        () => context.dispatch(new UpdateOrganizationSuccess()),
        (err) => context.dispatch(new UpdateOrganizationFail(err))
      );
  }

  @Action(GetOrganizationProfile)
  getOrganizationProfile(context: StateContext<OrganizationsStateModel>, { request }: GetOrganizationProfile) {
    context.patchState({ updating: true });

    this.organizationsService.getPublicProfile(request)
      .pipe(
      )
      .subscribe(
        (profile) => context.dispatch(new GetOrganizationProfileSuccess(profile)),
        (err) => context.dispatch(new GetOrganizationProfileFail(err))
      );
  }

  @Action(GetOrganizationProfileSuccess)
  getOrganizationProfileSuccess(context: StateContext<OrganizationsStateModel>, { organizationProfile }: GetOrganizationProfileSuccess) {
    context.patchState({ organizationProfile });
  }

  @Action(ClearOrganizationProfile)
  clearOrganizationProfile(context: StateContext<OrganizationsStateModel>, action: ClearOrganizationProfile) {
    context.patchState({ organizationProfile: undefined });
  }

  constructor(
    private organizationsService: OrganizationsService,
    private publishService: PublishService,
    private pendoService: PendoService
  ) { }
}
